/**
 * All fair times are Asia/Jerusalem wall-clock times. Nothing here depends on the
 * device's own time zone, so a student abroad sees the same phase as everyone else.
 */
export const FAIR_TZ = 'Asia/Jerusalem';
export const MIN = 60_000;

export const pad = (n: number | string): string => String(n).padStart(2, '0');

let dtf: Intl.DateTimeFormat | undefined;
function parts(ts: number): Record<string, number> {
  dtf ??= new Intl.DateTimeFormat('en-US', {
    timeZone: FAIR_TZ,
    hourCycle: 'h23',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  });
  const out: Record<string, number> = {};
  for (const p of dtf.formatToParts(new Date(ts))) {
    if (p.type !== 'literal') out[p.type] = Number(p.value);
  }
  return out;
}

/** Offset of Asia/Jerusalem from UTC at the given instant, in ms (+2h or +3h). */
export function tzOffsetMs(ts: number): number {
  const sec = Math.floor(ts / 1000) * 1000;
  const p = parts(sec);
  return Date.UTC(p.year!, p.month! - 1, p.day!, p.hour!, p.minute!, p.second!) - sec;
}

/** "+02:00" / "+03:00" for calendar links. */
export function ilOffset(ts: number): string {
  const min = Math.round(tzOffsetMs(ts) / MIN);
  const sign = min < 0 ? '-' : '+';
  const abs = Math.abs(min);
  return `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const HM_RE = /^(\d{1,2}):(\d{2})$/;

/** Epoch ms for a YYYY-MM-DD date and HH:MM time in Israel. NaN when either is invalid. */
export function toTs(date: string | undefined, hm: string | undefined): number {
  const d = DATE_RE.exec(date ?? '');
  const t = HM_RE.exec(hm ?? '');
  if (!d || !t) return NaN;
  const [y, mo, da] = [Number(d[1]), Number(d[2]), Number(d[3])];
  const [h, mi] = [Number(t[1]), Number(t[2])];
  if (h > 23 || mi > 59) return NaN;
  const guess = Date.UTC(y, mo - 1, da, h, mi);
  const check = new Date(guess);
  if (check.getUTCMonth() !== mo - 1 || check.getUTCDate() !== da) return NaN; // e.g. 2026-02-31
  // Two passes resolve the DST boundary: guess the offset at the UTC instant, then at the result.
  const first = guess - tzOffsetMs(guess);
  return guess - tzOffsetMs(first);
}

/** "HH:MM" in Israel. */
export function hhmm(ts: number): string {
  const p = parts(ts);
  return `${pad(p.hour!)}:${pad(p.minute!)}`;
}

/** "YYYY-MM-DD" in Israel. */
export function ilDate(ts: number): string {
  const p = parts(ts);
  return `${p.year}-${pad(p.month!)}-${pad(p.day!)}`;
}

/**
 * Client clock offset (server minus local) from an HTTP `Date` header.
 * The header has 1s resolution, so the request midpoint is used to cancel latency.
 */
export function clockOffsetFromDateHeader(
  dateHeader: string | null | undefined,
  requestStartMs: number,
  responseEndMs: number,
): number | null {
  if (!dateHeader) return null;
  const server = Date.parse(dateHeader);
  if (!Number.isFinite(server)) return null;
  // The header truncates to the second, so the true time is on average 500ms later.
  return server + 500 - (requestStartMs + responseEndMs) / 2;
}
